import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { CompaniesView } from './crm/CompaniesView';
import { ContactsView } from './crm/ContactsView';
import { DealsView } from './crm/DealsView';
import { AwaitingReplyView } from './crm/AwaitingReplyView';
import { LandingOfferView } from './crm/LandingOfferView';

// ============================================
// Types
// ============================================

interface Demo {
  id: number;
  scheduled_at: string;
  duration_mins: number | null;
  outcome: 'booked' | 'completed' | 'no_show' | 'cancelled';
  title: string | null;
  notes: string | null;
}

interface Prospect {
  id: number;
  full_name: string;
  email: string | null;
  company: string | null;
  company_id: number | null;
  job_title: string | null;
  linkedin_url: string | null;
  stage: string;
  stage_position: number | null;
  notes: string | null;
  source: string;
  legacy_label: string | null;
  priority: number;
  converted_user_id: number | null;
  converted_email: string | null;
  converted_at: string | null;
  lost_at: string | null;
  lost_reason: string | null;
  demos: Demo[] | null;
  last_demo_at: string | null;
  next_demo_at: string | null;
  created_at: string;
  updated_at: string;
}

interface Task {
  id: number;
  prospect_id: number;
  title: string;
  task_type: string;
  due_date: string;
  reminder_at: string | null;
  completed_at: string | null;
  notes: string | null;
  source_demo_id: number | null;
}

interface Stage {
  id: number;
  key: string;
  label: string;
  tone: string;
  description: string | null;
  display_order: number;
  is_won: boolean;
  is_lost: boolean;
}

const TONE_CLASSES: Record<string, string> = {
  blue:   'bg-blue-50 border-blue-200',
  purple: 'bg-purple-50 border-purple-200',
  amber:  'bg-amber-50 border-amber-200',
  green:  'bg-green-50 border-green-200',
  red:    'bg-red-50 border-red-200',
  gray:   'bg-gray-100 border-gray-300',
  pink:   'bg-pink-50 border-pink-200',
  indigo: 'bg-indigo-50 border-indigo-200',
  teal:   'bg-teal-50 border-teal-200'
};
const TONE_NAMES = Object.keys(TONE_CLASSES);

interface OutlookStatus {
  configured: boolean;
  connected: boolean;
  reason?: string;
  integration?: {
    id: number;
    outlook_email: string | null;
    demo_category_name: string;
    last_synced_at: string | null;
    last_error: string | null;
    last_error_at: string | null;
    sync_interval_minutes: number | null;
  } | null;
}

// ============================================
// Helpers
// ============================================

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const formatDaysAgo = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 0) return `in ${-days}d`;
  return `${days}d ago`;
};

// ============================================
// Main component
// ============================================

export const CRM: React.FC = () => {
  const { user } = useAuth();
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [outlookStatus, setOutlookStatus] = useState<OutlookStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Prospect | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [showStagesModal, setShowStagesModal] = useState(false);
  // Era filter: 'all' = everything, 'current' = no legacy_label,
  // or a specific legacy_label string (e.g. 'V1 Demo').
  const [eraFilter, setEraFilter] = useState<string>('all');
  // Board (kanban) vs List (table) view — persisted across sessions.
  const [viewMode, setViewMode] = useState<'board' | 'list'>(
    () => (localStorage.getItem('crm.viewMode') as 'board' | 'list') || 'board'
  );
  useEffect(() => { localStorage.setItem('crm.viewMode', viewMode); }, [viewMode]);
  // Search box in list view
  const [listSearch, setListSearch] = useState('');
  // Top-level CRM tab — Pipeline (existing kanban) / Companies / Contacts / Deals
  const [tab, setTab] = useState<'pipeline' | 'companies' | 'contacts' | 'deals' | 'awaiting' | 'landing'>(
    () => (localStorage.getItem('crm.tab') as any) || 'pipeline'
  );
  useEffect(() => { localStorage.setItem('crm.tab', tab); }, [tab]);

  const loadBoard = useCallback(async () => {
    try {
      const [boardRes, stagesRes] = await Promise.all([
        fetch('/api/crm/board',  { headers: authHeaders() }),
        fetch('/api/crm/stages', { headers: authHeaders() })
      ]);
      if (!boardRes.ok)  throw new Error('Failed to load CRM board');
      if (!stagesRes.ok) throw new Error('Failed to load pipeline stages');
      const board = await boardRes.json();
      const stg   = await stagesRes.json();
      setProspects(board.prospects || []);
      setStages(stg.stages || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOutlookStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/crm/outlook/status', { headers: authHeaders() });
      if (res.ok) setOutlookStatus(await res.json());
    } catch (err) { /* best effort */ }
  }, []);

  useEffect(() => {
    loadBoard();
    loadOutlookStatus();

    // Surface URL params from OAuth callback
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('outlookConnected') === '1') {
      setError(null);
      setTimeout(() => loadOutlookStatus(), 200);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (qs.get('outlookError')) {
      setError(`Outlook connection failed: ${qs.get('outlookError')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loadBoard, loadOutlookStatus]);

  // Deep-link: if the URL has ?contact=:id (e.g. user clicked a
  // contact from the company detail page), open that contact's
  // modal once the board has loaded.
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const contactId = qs.get('contact');
    if (!contactId) return;
    if (prospects.length === 0) return; // wait for board
    const target = prospects.find(p => p.id === parseInt(contactId, 10));
    if (target) {
      setEditing(target);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [prospects]);

  // Distinct legacy labels present in the data — drives the era toggle.
  const legacyLabels = useMemo(() => {
    const set = new Set<string>();
    for (const p of prospects) if (p.legacy_label) set.add(p.legacy_label);
    return Array.from(set).sort();
  }, [prospects]);

  // Filter prospects by era, then group by stage. Sorted within each
  // column by priority desc, then most recent updated.
  const byStage = useMemo(() => {
    const filtered = prospects.filter(p => {
      if (eraFilter === 'all') return true;
      if (eraFilter === 'current') return !p.legacy_label;
      return p.legacy_label === eraFilter;
    });
    const grouped: Record<string, Prospect[]> = {};
    for (const s of stages) grouped[s.key] = [];
    for (const p of filtered) {
      if (!grouped[p.stage]) grouped[p.stage] = [];
      grouped[p.stage].push(p);
    }
    for (const key of Object.keys(grouped)) {
      grouped[key].sort((a, b) => {
        if ((b.priority || 0) !== (a.priority || 0)) return (b.priority || 0) - (a.priority || 0);
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });
    }
    return grouped;
  }, [prospects, stages, eraFilter]);

  // Set a prospect's priority directly (called from the popover picker).
  // Optimistic UI; reload on failure.
  const setProspectPriority = async (prospectId: number, next: number) => {
    setProspects(prev => prev.map(p => p.id === prospectId ? { ...p, priority: next } : p));
    try {
      await fetch(`/api/crm/prospects/${prospectId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ priority: next })
      });
    } catch {
      loadBoard();
    }
  };

  // Drag-and-drop handlers
  const onDragStart = (e: React.DragEvent, prospectId: number) => {
    setDraggingId(prospectId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(prospectId));
  };

  const onDragOver = (e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stageKey) setDragOverStage(stageKey);
  };

  const onDrop = async (e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    setDragOverStage(null);
    const id = parseInt(e.dataTransfer.getData('text/plain'), 10);
    setDraggingId(null);
    if (!id) return;

    const prospect = prospects.find(p => p.id === id);
    if (!prospect || prospect.stage === stageKey) return;

    // Optimistic update
    setProspects(prev => prev.map(p => p.id === id ? { ...p, stage: stageKey } : p));

    try {
      const res = await fetch(`/api/crm/prospects/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ stage: stageKey })
      });
      if (!res.ok) throw new Error('Stage update failed');
      const data = await res.json();
      setProspects(prev => prev.map(p => p.id === id ? data.prospect : p));
    } catch (err: any) {
      setError(err.message);
      loadBoard(); // revert on failure
    }
  };

  const connectOutlook = async () => {
    try {
      const res = await fetch('/api/crm/outlook/connect', { headers: authHeaders() });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to initiate Outlook connection');
      }
      const data = await res.json();
      window.location.href = data.authorizeUrl;
    } catch (err: any) {
      setError(err.message);
    }
  };

  const disconnectOutlook = async () => {
    if (!window.confirm('Disconnect Outlook? Synced prospects will remain, but new demos won\'t auto-create.')) return;
    await fetch('/api/crm/outlook/disconnect', { method: 'POST', headers: authHeaders() });
    loadOutlookStatus();
  };

  const triggerSync = async (opts?: { lookbackDays?: number; pullAllMeetings?: boolean }) => {
    setSyncing(true);
    try {
      const res = await fetch('/api/crm/outlook/sync', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(opts || {})
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Sync failed');
      }
      const result = await res.json();
      await loadBoard();
      await loadOutlookStatus();
      // Rich summary so the user can self-diagnose 0/0/0 results.
      const win = `last ${result.lookbackDays}d`;
      const filterDesc = result.mode === 'all_external_meetings'
        ? 'with external attendees'
        : `tagged "${result.categoryFilter}"`;
      const headline = `Synced (${win}) — scanned ${result.eventsScanned} events, ${result.demoEvents} ${filterDesc}.`;
      const autoWonBit = result.createdProspectsAutoWon > 0
        ? ` ${result.createdProspectsAutoWon} auto-matched to existing customers (placed in Won).`
        : '';
      const detail = ` Created ${result.createdProspects} new prospects, ${result.createdDemos} demos, ${result.skipped} already synced.${autoWonBit}`;
      let extra = '';
      if (result.eventsScanned > 0 && result.demoEvents === 0 && result.mode === 'category') {
        const seen = (result.categoriesSeen || []).filter((c: string) => c).slice(0, 8);
        extra = seen.length > 0
          ? ` No events tagged "${result.categoryFilter}". Categories actually found: ${seen.map((c: string) => `"${c}"`).join(', ')}. Try "Pull all external meetings" from the ▾ menu to backfill without tagging.`
          : ` No events have any categories set. Try "Pull all external meetings" from the ▾ menu — it'll grab every meeting with an outside attendee.`;
      }
      if (result.eventsScanned > 0 && result.demoEvents === 0 && result.mode === 'all_external_meetings') {
        extra = ` Looks like none of your meetings in this window had any external attendees (everyone was @your-domain). Try a longer window.`;
      }
      setError(headline + detail + extra);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading CRM…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[1600px] mx-auto space-y-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">CRM Pipeline</h1>
            <p className="text-sm text-gray-600 mt-1">
              {prospects.length} prospects · {prospects.filter(p => p.stage === 'customer').length} customers ·
              {' '}{prospects.filter(p => p.stage === 'demo_booked').length} demos booked
            </p>
          </div>
          <div className="flex items-center gap-2">
            <OutlookConnectButton
              status={outlookStatus}
              syncing={syncing}
              onConnect={connectOutlook}
              onDisconnect={disconnectOutlook}
              onSync={triggerSync}
            />
            <button
              onClick={() => setShowAdd(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Add Prospect
            </button>
            <button
              onClick={() => setShowStagesModal(true)}
              className="px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              title="Manage pipeline stages"
            >
              ⚙ Stages
            </button>
            <button
              onClick={() => window.location.href = '/admin/command-center'}
              className="px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
            >
              Command Center
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-blue-600 underline text-xs">dismiss</button>
          </div>
        )}

        {/* Top-level tab bar */}
        <div className="bg-white rounded-lg shadow px-2 py-1.5 flex items-center gap-1 flex-wrap">
          <CrmTabBtn active={tab === 'pipeline'}  onClick={() => setTab('pipeline')}  label="Pipeline"  hint="Drag-and-drop kanban" />
          <CrmTabBtn active={tab === 'companies'} onClick={() => setTab('companies')} label="Companies" hint="Organisations" />
          <CrmTabBtn active={tab === 'contacts'}  onClick={() => setTab('contacts')}  label="Contacts"  hint="People" />
          <CrmTabBtn active={tab === 'deals'}     onClick={() => setTab('deals')}     label="Deals"     hint="Opportunities" />
          <CrmTabBtn active={tab === 'awaiting'}  onClick={() => setTab('awaiting')}  label="🔥 Awaiting reply" hint="Threads where the prospect emailed and you haven't responded" />
          <CrmTabBtn active={tab === 'landing'}   onClick={() => setTab('landing')}   label="🎁 Landing page offer" hint="Free-market-map interest from marketmapr.com/free-market" />
        </div>

        {tab === 'companies' && <CompaniesView />}
        {tab === 'contacts'  && <ContactsView />}
        {tab === 'deals'     && <DealsView stages={stages} />}
        {tab === 'awaiting'  && <AwaitingReplyView />}
        {tab === 'landing'   && <LandingOfferView />}

        {tab === 'pipeline' && <>
        {/* Era filter (V1 / V2 / All) + retro-label trigger + view toggle */}
        <EraToggle
          eraFilter={eraFilter}
          setEraFilter={setEraFilter}
          legacyLabels={legacyLabels}
          totalCount={prospects.length}
          onRetroLabel={() => loadBoard()}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />

        {viewMode === 'list' && (
          <ProspectListView
            prospects={Object.values(byStage).flat()}
            stages={stages}
            search={listSearch}
            setSearch={setListSearch}
            onOpenProspect={(p) => setEditing(p)}
            onSetPriority={setProspectPriority}
          />
        )}

        {viewMode === 'board' && (
        /* Kanban board */
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(0, 1fr))` }}
        >
          {stages.map(stage => {
            const cards = byStage[stage.key] || [];
            const isDragOver = dragOverStage === stage.key;
            return (
              <div
                key={stage.key}
                onDragOver={e => onDragOver(e, stage.key)}
                onDragLeave={() => setDragOverStage(null)}
                onDrop={e => onDrop(e, stage.key)}
                className={`rounded-lg border-2 transition-colors ${TONE_CLASSES[stage.tone] || TONE_CLASSES.gray} ${
                  isDragOver ? 'ring-2 ring-blue-400 ring-offset-1' : ''
                }`}
              >
                <div className="px-3 py-2 border-b border-current/10 sticky top-0 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 text-sm">{stage.label}</h3>
                    <span className="text-xs text-gray-500 font-medium">{cards.length}</span>
                  </div>
                  {stage.description && (
                    <p className="text-[10px] text-gray-500 mt-0.5">{stage.description}</p>
                  )}
                </div>
                <div className="p-2 space-y-2 min-h-[300px]">
                  {cards.map(card => (
                    <ProspectCard
                      key={card.id}
                      prospect={card}
                      isDragging={draggingId === card.id}
                      onDragStart={onDragStart}
                      onClick={() => setEditing(card)}
                      onSetPriority={(n) => setProspectPriority(card.id, n)}
                    />
                  ))}
                  {cards.length === 0 && (
                    <div className="text-xs text-gray-400 text-center py-6 italic">
                      {isDragOver ? 'Drop here' : 'No prospects'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        )}
        </>}
      </div>

      {/* Modals */}
      {showAdd && (
        <ProspectModal
          stages={stages}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); loadBoard(); }}
        />
      )}
      {editing && (
        <ProspectModal
          prospect={editing}
          stages={stages}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadBoard(); }}
          onDeleted={() => { setEditing(null); loadBoard(); }}
        />
      )}
      {showStagesModal && (
        <StagesModal
          stages={stages}
          onClose={() => setShowStagesModal(false)}
          onChanged={() => loadBoard()}
        />
      )}
    </div>
  );
};

// ============================================
// Prospect card
// ============================================

const ProspectCard: React.FC<{
  prospect: Prospect;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, id: number) => void;
  onClick: () => void;
  onSetPriority: (n: number) => void;
}> = ({ prospect, isDragging, onDragStart, onClick, onSetPriority }) => {
  const nextDemoSoon = prospect.next_demo_at &&
    (new Date(prospect.next_demo_at).getTime() - Date.now()) < 7 * 86400000;

  // Show company prominently when we have one — the contact-name field
  // can be messy ("Alex + Joe — Marketmapr Demo" if it was parsed from
  // a calendar subject), but the company is derived from the email
  // domain and is usually clean and the more useful identifier on a
  // pipeline card. Falls back to contact name when no company is known.
  const headline = prospect.company || prospect.full_name || 'Untitled';
  const subline  = prospect.company ? prospect.full_name : null;

  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, prospect.id)}
      onClick={onClick}
      className={`bg-white rounded-md border border-gray-200 shadow-sm p-3 text-sm cursor-pointer hover:shadow-md transition-shadow ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-gray-900 truncate">{headline}</div>
          {subline && (
            <div className="text-xs text-gray-700 truncate mt-0.5">
              {prospect.job_title ? `${prospect.job_title} — ` : ''}{subline}
            </div>
          )}
        </div>
        <PriorityPicker priority={prospect.priority || 0} onChange={onSetPriority} />
      </div>
      {prospect.email && (
        <div className="text-xs text-gray-500 truncate mt-0.5">{prospect.email}</div>
      )}
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {prospect.legacy_label && (
          <span className="inline-block px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-medium">
            {prospect.legacy_label}
          </span>
        )}
        {prospect.source === 'outlook_sync' && (
          <span className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">
            📅 Outlook
          </span>
        )}
        {prospect.next_demo_at && (
          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
            nextDemoSoon ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
          }`}>
            {formatDateTime(prospect.next_demo_at)}
          </span>
        )}
        {!prospect.next_demo_at && prospect.last_demo_at && (
          <span className="inline-block px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px]">
            last demo {formatDaysAgo(prospect.last_demo_at)}
          </span>
        )}
      </div>
      {prospect.notes && (
        <p className="text-xs text-gray-500 mt-2 line-clamp-2">{prospect.notes}</p>
      )}
    </div>
  );
};

// ============================================
// Outlook connection button
// ============================================

const OutlookConnectButton: React.FC<{
  status: OutlookStatus | null;
  syncing: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onSync: (opts?: { lookbackDays?: number; pullAllMeetings?: boolean; legacyCutoffDate?: string; legacyLabel?: string }) => void;
}> = ({ status, syncing, onConnect, onDisconnect, onSync }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  // Optional product-era split. Persisted in localStorage so the user
  // doesn't have to re-enter on every backfill click.
  const [legacyCutoff, setLegacyCutoff] = useState<string>(() => localStorage.getItem('crm.legacyCutoff') || '');
  const [legacyLabel,  setLegacyLabel]  = useState<string>(() => localStorage.getItem('crm.legacyLabel')  || '');
  useEffect(() => { localStorage.setItem('crm.legacyCutoff', legacyCutoff); }, [legacyCutoff]);
  useEffect(() => { localStorage.setItem('crm.legacyLabel',  legacyLabel);  }, [legacyLabel]);

  const pullOpts = (lookbackDays: number, all: boolean) => {
    const opts: any = { lookbackDays };
    if (all) opts.pullAllMeetings = true;
    if (legacyCutoff && legacyLabel.trim()) {
      opts.legacyCutoffDate = legacyCutoff;
      opts.legacyLabel = legacyLabel.trim();
    }
    return opts;
  };
  if (!status) return null;

  if (!status.configured) {
    return (
      <span className="text-xs text-gray-500 italic px-3 py-2">
        Outlook: not configured
      </span>
    );
  }

  if (!status.connected) {
    return (
      <button
        onClick={onConnect}
        className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700"
      >
        📅 Connect Outlook
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-md text-xs">
      <span className="text-green-700">📅</span>
      <div>
        <div className="font-medium text-green-900">{status.integration?.outlook_email}</div>
        <div className="text-green-600 text-[10px]">
          {status.integration?.last_synced_at
            ? `synced ${formatDaysAgo(status.integration.last_synced_at)}`
            : 'never synced'}
          {status.integration?.last_error && ' — error'}
        </div>
      </div>
      <div className="relative">
        <button
          onClick={() => onSync()}
          disabled={syncing}
          className="px-2 py-1 bg-green-600 text-white rounded-l text-[11px] hover:bg-green-700 disabled:opacity-50"
        >
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
        <button
          onClick={() => setMenuOpen(o => !o)}
          disabled={syncing}
          className="px-1.5 py-1 bg-green-700 text-white rounded-r text-[11px] hover:bg-green-800 disabled:opacity-50 border-l border-green-800"
          title="Backfill options"
        >
          ▾
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-10 text-xs w-80">
            <div className="px-3 py-2 bg-amber-50/50 border-b border-gray-100">
              <div className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold mb-1.5">
                Optional: product-era split
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-500">Before</span>
                <input
                  type="date"
                  value={legacyCutoff}
                  onChange={e => setLegacyCutoff(e.target.value)}
                  className="flex-1 px-1.5 py-1 border border-gray-300 rounded text-[11px]"
                />
                <span className="text-[10px] text-gray-500">tag as</span>
                <input
                  type="text"
                  placeholder="V1 Demo"
                  value={legacyLabel}
                  onChange={e => setLegacyLabel(e.target.value)}
                  className="w-20 px-1.5 py-1 border border-gray-300 rounded text-[11px]"
                />
              </div>
              <p className="text-[10px] text-gray-500 mt-1">Saved locally — leave blank to skip.</p>
            </div>

            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold bg-gray-50 border-b">
              Tagged "Demo" only
            </div>
            <button
              onClick={() => { setMenuOpen(false); onSync(pullOpts(60, false)); }}
              className="block w-full text-left px-3 py-2 hover:bg-gray-50"
            >
              Sync — last 60 days <span className="text-gray-400">(default)</span>
            </button>
            <button
              onClick={() => { setMenuOpen(false); onSync(pullOpts(365, false)); }}
              className="block w-full text-left px-3 py-2 hover:bg-gray-50"
            >
              Backfill — last 12 months
            </button>
            <button
              onClick={() => { setMenuOpen(false); onSync(pullOpts(730, false)); }}
              className="block w-full text-left px-3 py-2 hover:bg-gray-50"
            >
              Backfill — last 2 years
            </button>

            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold bg-gray-50 border-y">
              All external meetings <span className="text-gray-400 normal-case">(no tagging needed)</span>
            </div>
            <button
              onClick={() => { setMenuOpen(false); onSync(pullOpts(180, true)); }}
              className="block w-full text-left px-3 py-2 hover:bg-blue-50 text-blue-900"
            >
              Pull all external — last 6 months
            </button>
            <button
              onClick={() => { setMenuOpen(false); onSync(pullOpts(365, true)); }}
              className="block w-full text-left px-3 py-2 hover:bg-blue-50 text-blue-900"
            >
              Pull all external — last 12 months
            </button>
            <button
              onClick={() => { setMenuOpen(false); onSync(pullOpts(730, true)); }}
              className="block w-full text-left px-3 py-2 hover:bg-blue-50 text-blue-900"
            >
              Pull all external — last 2 years
            </button>
            <button
              onClick={() => { setMenuOpen(false); onSync(pullOpts(1825, true)); }}
              className="block w-full text-left px-3 py-2 hover:bg-blue-50 text-blue-900 border-t border-gray-100"
            >
              Pull all external — last 5 years <span className="text-gray-400">(max)</span>
            </button>
          </div>
        )}
      </div>
      <button
        onClick={onDisconnect}
        className="text-green-700 hover:text-green-900 text-xs underline"
      >
        disconnect
      </button>
    </div>
  );
};

// ============================================
// Add / edit modal
// ============================================

const ProspectModal: React.FC<{
  prospect?: Prospect;
  stages: Stage[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
}> = ({ prospect, stages, onClose, onSaved, onDeleted }) => {
  const [fullName, setFullName]   = useState(prospect?.full_name || '');
  const [email, setEmail]         = useState(prospect?.email || '');
  const [company, setCompany]     = useState(prospect?.company || '');
  const [jobTitle, setJobTitle]   = useState(prospect?.job_title || '');
  const [linkedin, setLinkedin]   = useState(prospect?.linkedin_url || '');
  const [stage, setStage]         = useState(prospect?.stage || stages[0]?.key || 'demo_booked');
  const [notes, setNotes]         = useState(prospect?.notes || '');
  const [saving, setSaving]       = useState(false);
  const [showAddDemo, setShowAddDemo] = useState(false);
  const [demoDate, setDemoDate]       = useState('');
  const [demoTitle, setDemoTitle]     = useState('');
  const [demoNotes, setDemoNotes]     = useState('');

  const isEdit = !!prospect;

  const save = async () => {
    if (!fullName.trim()) return;
    setSaving(true);
    try {
      const body = JSON.stringify({
        fullName: fullName.trim(),
        email: email.trim() || null,
        company: company.trim() || null,
        jobTitle: jobTitle.trim() || null,
        linkedinUrl: linkedin.trim() || null,
        stage,
        notes: notes.trim() || null
      });
      const res = await fetch(
        isEdit ? `/api/crm/prospects/${prospect!.id}` : '/api/crm/prospects',
        { method: isEdit ? 'PATCH' : 'POST', headers: authHeaders(), body }
      );
      if (!res.ok) throw new Error('Save failed');
      onSaved();
    } catch (err: any) {
      window.alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addDemo = async () => {
    if (!demoDate || !prospect) return;
    try {
      const res = await fetch(`/api/crm/prospects/${prospect.id}/demos`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          scheduledAt: new Date(demoDate).toISOString(),
          title: demoTitle.trim() || null,
          notes: demoNotes.trim() || null,
          outcome: 'booked'
        })
      });
      if (!res.ok) throw new Error('Failed to log demo');
      setShowAddDemo(false);
      setDemoDate(''); setDemoTitle(''); setDemoNotes('');
      onSaved(); // reloads board which also reloads demos
    } catch (err: any) {
      window.alert(err.message);
    }
  };

  const updateDemoOutcome = async (demoId: number, outcome: Demo['outcome']) => {
    try {
      const res = await fetch(`/api/crm/demos/${demoId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ outcome })
      });
      if (!res.ok) throw new Error('Update failed');
      onSaved();
    } catch (err: any) {
      window.alert(err.message);
    }
  };

  const del = async () => {
    if (!prospect) return;
    if (!window.confirm(`Delete ${prospect.full_name}? This also removes their demos.`)) return;
    await fetch(`/api/crm/prospects/${prospect.id}`, { method: 'DELETE', headers: authHeaders() });
    onDeleted?.();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Prospect' : 'Add Prospect'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Full name *" value={fullName} onChange={setFullName} />
            <Field label="Email" value={email} onChange={setEmail} type="email" />
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-700">Company</label>
                {prospect?.company_id && (
                  <a
                    href={`/admin/crm/companies/${prospect.company_id}`}
                    className="text-[10px] text-blue-600 hover:text-blue-800 underline"
                  >
                    Open record →
                  </a>
                )}
              </div>
              <input
                type="text"
                value={company}
                onChange={e => setCompany(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <Field label="Job title" value={jobTitle} onChange={setJobTitle} />
            <Field label="LinkedIn URL" value={linkedin} onChange={setLinkedin} />
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Stage</label>
              <select
                value={stage}
                onChange={e => setStage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Demo notes, follow-up actions, anything useful…"
            />
          </div>

          {isEdit && prospect && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900">Demos ({prospect.demos?.length || 0})</h3>
                <button
                  onClick={() => setShowAddDemo(!showAddDemo)}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {showAddDemo ? 'cancel' : '+ log demo'}
                </button>
              </div>
              {showAddDemo && (
                <div className="bg-gray-50 rounded-md p-3 mb-3 space-y-2">
                  <input
                    type="datetime-local"
                    value={demoDate}
                    onChange={e => setDemoDate(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Title (optional)"
                    value={demoTitle}
                    onChange={e => setDemoTitle(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                  <textarea
                    placeholder="Notes (optional)"
                    value={demoNotes}
                    onChange={e => setDemoNotes(e.target.value)}
                    rows={2}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                  <button
                    onClick={addDemo}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    Save demo
                  </button>
                </div>
              )}
              <div className="space-y-1.5">
                {(prospect.demos || []).map(d => (
                  <div key={d.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-3 py-2">
                    <div>
                      <div className="font-medium text-gray-900">{d.title || 'Untitled demo'}</div>
                      <div className="text-gray-500">{formatDateTime(d.scheduled_at)}{d.duration_mins ? ` · ${d.duration_mins} min` : ''}</div>
                      {d.notes && <p className="text-gray-600 mt-1">{d.notes}</p>}
                    </div>
                    <select
                      value={d.outcome}
                      onChange={e => updateDemoOutcome(d.id, e.target.value as Demo['outcome'])}
                      className={`text-[10px] border rounded px-1.5 py-0.5 ${
                        d.outcome === 'completed' ? 'bg-green-50 text-green-800 border-green-200' :
                        d.outcome === 'no_show'   ? 'bg-red-50 text-red-800 border-red-200' :
                        d.outcome === 'cancelled' ? 'bg-gray-100 text-gray-600 border-gray-300' :
                        'bg-blue-50 text-blue-800 border-blue-200'
                      }`}
                    >
                      <option value="booked">Booked</option>
                      <option value="completed">Completed</option>
                      <option value="no_show">No-show</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                ))}
                {(prospect.demos?.length || 0) === 0 && (
                  <p className="text-xs text-gray-400 italic">No demos logged yet.</p>
                )}
              </div>
            </div>
          )}

          {isEdit && prospect && (
            <TasksPanel prospectId={prospect.id} />
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center sticky bottom-0 bg-white">
          {isEdit ? (
            <button onClick={del} className="text-red-600 hover:text-red-800 text-sm">Delete prospect</button>
          ) : <div></div>}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm">Cancel</button>
            <button
              onClick={save}
              disabled={saving || !fullName.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Priority picker (popover, no cycle-on-click)
// ============================================
// Cycle-on-click caused cards to jump position before you could land
// on the value you wanted (the board re-sorts by priority desc on every
// change). This opens a small popover and lets you pick the target
// value directly — single click, single re-sort.

const PriorityPicker: React.FC<{
  priority: number;
  onChange: (next: number) => void;
  size?: 'sm' | 'md';
}> = ({ priority, onChange, size = 'sm' }) => {
  const [open, setOpen] = useState(false);
  const isSet = priority > 0;

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);

  return (
    <div className="relative shrink-0">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        title={isSet ? `Priority ${priority}/5 — click to change` : 'Set priority'}
        className={`leading-none ${size === 'sm' ? 'text-[11px]' : 'text-sm'} ${
          isSet ? '' : 'opacity-30 hover:opacity-60'
        }`}
      >
        {isSet ? '🔥'.repeat(priority) : '🔥'}
      </button>
      {open && (
        <div
          onClick={e => e.stopPropagation()}
          className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-20 p-1 flex items-center gap-0.5"
        >
          <button
            onClick={() => { onChange(0); setOpen(false); }}
            className="px-2 py-1 text-[10px] text-gray-500 hover:bg-gray-100 rounded"
            title="Clear priority"
          >
            ✕
          </button>
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              onClick={() => { onChange(n); setOpen(false); }}
              className={`px-1.5 py-1 rounded leading-none ${
                priority === n ? 'bg-amber-100' : 'hover:bg-amber-50'
              }`}
              title={`Priority ${n}/5`}
            >
              <span className="text-xs">{'🔥'.repeat(n)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// Era toggle (V1 / V2 / All)
// ============================================

const EraToggle: React.FC<{
  eraFilter: string;
  setEraFilter: (v: string) => void;
  legacyLabels: string[];
  totalCount: number;
  onRetroLabel: () => void;
  viewMode: 'board' | 'list';
  setViewMode: (v: 'board' | 'list') => void;
}> = ({ eraFilter, setEraFilter, legacyLabels, totalCount, onRetroLabel, viewMode, setViewMode }) => {
  const [showRetro, setShowRetro] = useState(false);
  const [cutoff, setCutoff] = useState(() => localStorage.getItem('crm.legacyCutoff') || '');
  const [label, setLabel] = useState(() => localStorage.getItem('crm.legacyLabel') || 'V1 Demo');
  const [overwrite, setOverwrite] = useState(false);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const applyRetroLabel = async () => {
    if (!cutoff || !label.trim()) { window.alert('Set both date and label first.'); return; }
    setWorking(true);
    setResult(null);
    try {
      localStorage.setItem('crm.legacyCutoff', cutoff);
      localStorage.setItem('crm.legacyLabel', label);
      const res = await fetch('/api/crm/prospects/label-legacy', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ cutoffDate: cutoff, label: label.trim(), overwrite })
      });
      if (!res.ok) {
        const e = await res.json();
        throw new Error(e.error || 'Failed');
      }
      const data = await res.json();
      setResult(`Labelled ${data.updated} prospects as "${data.label}".`);
      onRetroLabel();
    } catch (err: any) {
      setResult(`Error: ${err.message}`);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-3 flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-1 bg-gray-100 rounded-md p-1">
        <EraButton active={eraFilter === 'all'}      onClick={() => setEraFilter('all')}      label={`All (${totalCount})`} />
        <EraButton active={eraFilter === 'current'}  onClick={() => setEraFilter('current')}  label="Current (V2)" />
        {legacyLabels.map(l => (
          <EraButton key={l} active={eraFilter === l} onClick={() => setEraFilter(l)} label={l} />
        ))}
      </div>

      <div className="flex items-center gap-1 bg-gray-100 rounded-md p-1">
        <EraButton active={viewMode === 'board'} onClick={() => setViewMode('board')} label="◫ Board" />
        <EraButton active={viewMode === 'list'}  onClick={() => setViewMode('list')}  label="☰ List" />
      </div>

      <div className="ml-auto flex items-center gap-3">
        <CleanupParsedButton onDone={onRetroLabel} />
        <button
          onClick={() => setShowRetro(s => !s)}
          className="text-xs text-blue-600 hover:text-blue-800 underline"
        >
          {showRetro ? 'Hide' : 'Retroactively label existing prospects'}
        </button>
      </div>

      {showRetro && (
        <div className="w-full bg-amber-50 border border-amber-200 rounded-md p-3 flex flex-wrap items-end gap-2 text-xs">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-600 font-semibold mb-0.5">
              Earliest demo before
            </label>
            <input
              type="date"
              value={cutoff}
              onChange={e => setCutoff(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-xs"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-600 font-semibold mb-0.5">
              Label
            </label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-xs w-28"
            />
          </div>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} />
            <span>Overwrite existing labels</span>
          </label>
          <button
            onClick={applyRetroLabel}
            disabled={working || !cutoff || !label.trim()}
            className="px-3 py-1.5 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700 disabled:opacity-50"
          >
            {working ? 'Applying…' : 'Apply to existing prospects'}
          </button>
          {result && <span className="text-gray-700">{result}</span>}
        </div>
      )}
    </div>
  );
};

// ============================================
// Prospect list view (table — alternative to kanban)
// ============================================
// Sortable columns, search by name/email/company. Click a row to open
// the same edit modal used by the kanban. Inline priority picker so
// you can triage from either view.

type SortKey = 'company' | 'name' | 'stage' | 'priority' | 'last_demo' | 'next_demo';
type SortDir = 'asc' | 'desc';

const ProspectListView: React.FC<{
  prospects: Prospect[];
  stages: Stage[];
  search: string;
  setSearch: (v: string) => void;
  onOpenProspect: (p: Prospect) => void;
  onSetPriority: (id: number, n: number) => void;
}> = ({ prospects, stages, search, setSearch, onOpenProspect, onSetPriority }) => {
  const [sortKey, setSortKey] = useState<SortKey>('priority');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'priority' || key === 'last_demo' || key === 'next_demo' ? 'desc' : 'asc'); }
  };

  const stageLabel = (key: string) => stages.find(s => s.key === key)?.label || key;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = !q ? prospects : prospects.filter(p =>
      (p.full_name || '').toLowerCase().includes(q) ||
      (p.email     || '').toLowerCase().includes(q) ||
      (p.company   || '').toLowerCase().includes(q) ||
      (p.notes     || '').toLowerCase().includes(q)
    );
    const sorted = [...list];
    const cmp = (a: any, b: any) => {
      const av = a ?? '', bv = b ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv));
    };
    sorted.sort((a, b) => {
      let res = 0;
      switch (sortKey) {
        case 'company':   res = cmp(a.company || '', b.company || ''); break;
        case 'name':      res = cmp(a.full_name || '', b.full_name || ''); break;
        case 'stage':     res = cmp(a.stage, b.stage); break;
        case 'priority':  res = (a.priority || 0) - (b.priority || 0); break;
        case 'last_demo': res = (a.last_demo_at ? new Date(a.last_demo_at).getTime() : 0)
                              - (b.last_demo_at ? new Date(b.last_demo_at).getTime() : 0); break;
        case 'next_demo': res = (a.next_demo_at ? new Date(a.next_demo_at).getTime() : Infinity)
                              - (b.next_demo_at ? new Date(b.next_demo_at).getTime() : Infinity); break;
      }
      return sortDir === 'asc' ? res : -res;
    });
    return sorted;
  }, [prospects, search, sortKey, sortDir]);

  const arrow = (k: SortKey) => sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-3 border-b border-gray-100">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, company, email, or notes…"
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
        <div className="text-xs text-gray-500 mt-1.5">
          Showing {filtered.length} of {prospects.length} prospects
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
            <tr>
              <th className="py-2 px-3 cursor-pointer hover:text-gray-900" onClick={() => toggleSort('company')}>Company{arrow('company')}</th>
              <th className="py-2 px-3 cursor-pointer hover:text-gray-900" onClick={() => toggleSort('name')}>Contact{arrow('name')}</th>
              <th className="py-2 px-3">Email</th>
              <th className="py-2 px-3 cursor-pointer hover:text-gray-900" onClick={() => toggleSort('stage')}>Stage{arrow('stage')}</th>
              <th className="py-2 px-3 cursor-pointer hover:text-gray-900 text-center" onClick={() => toggleSort('priority')}>Priority{arrow('priority')}</th>
              <th className="py-2 px-3 cursor-pointer hover:text-gray-900" onClick={() => toggleSort('last_demo')}>Last demo{arrow('last_demo')}</th>
              <th className="py-2 px-3 cursor-pointer hover:text-gray-900" onClick={() => toggleSort('next_demo')}>Next demo{arrow('next_demo')}</th>
              <th className="py-2 px-3">Tags</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(p => (
              <tr key={p.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onOpenProspect(p)}>
                <td className="py-2 px-3 font-medium text-gray-900">{p.company || '—'}</td>
                <td className="py-2 px-3 text-gray-700">
                  {p.full_name}
                  {p.job_title && <div className="text-xs text-gray-500">{p.job_title}</div>}
                </td>
                <td className="py-2 px-3 text-gray-500 truncate max-w-[200px]">{p.email || '—'}</td>
                <td className="py-2 px-3 text-gray-700">{stageLabel(p.stage)}</td>
                <td className="py-2 px-3 text-center" onClick={e => e.stopPropagation()}>
                  <PriorityPicker
                    priority={p.priority || 0}
                    onChange={(n) => onSetPriority(p.id, n)}
                    size="md"
                  />
                </td>
                <td className="py-2 px-3 text-xs text-gray-500">{formatDaysAgo(p.last_demo_at)}</td>
                <td className="py-2 px-3 text-xs text-gray-500">{p.next_demo_at ? formatDateTime(p.next_demo_at) : '—'}</td>
                <td className="py-2 px-3">
                  <div className="flex flex-wrap gap-1">
                    {p.legacy_label && (
                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-medium">{p.legacy_label}</span>
                    )}
                    {p.source === 'outlook_sync' && (
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">📅</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center text-gray-400 py-8 italic">No prospects match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================
// Tasks panel (inside prospect modal)
// ============================================

const TasksPanel: React.FC<{ prospectId: number }> = ({ prospectId }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState('');
  const [newNotes, setNewNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/crm/tasks?prospectId=${prospectId}&includeCompleted=1`, { headers: authHeaders() });
      const data = await res.json();
      setTasks(data.tasks || []);
    } finally {
      setLoading(false);
    }
  }, [prospectId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (t: Task) => {
    await fetch(`/api/crm/tasks/${t.id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ completed: !t.completed_at })
    });
    load();
  };

  const add = async () => {
    if (!newTitle.trim() || !newDue) return;
    await fetch(`/api/crm/prospects/${prospectId}/tasks`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        title: newTitle.trim(),
        dueDate: new Date(newDue).toISOString(),
        notes: newNotes.trim() || null
      })
    });
    setNewTitle(''); setNewDue(''); setNewNotes('');
    setShowAdd(false);
    load();
  };

  const del = async (t: Task) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    await fetch(`/api/crm/tasks/${t.id}`, { method: 'DELETE', headers: authHeaders() });
    load();
  };

  const openTasks      = tasks.filter(t => !t.completed_at);
  const completedTasks = tasks.filter(t =>  t.completed_at);

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">
          Tasks ({openTasks.length} open{completedTasks.length > 0 ? `, ${completedTasks.length} done` : ''})
        </h3>
        <button
          onClick={() => setShowAdd(s => !s)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          {showAdd ? 'cancel' : '+ add task'}
        </button>
      </div>
      {showAdd && (
        <div className="bg-gray-50 rounded-md p-3 mb-3 space-y-2">
          <input
            type="text"
            placeholder="What needs doing? (e.g. Send pricing proposal)"
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
          />
          <input
            type="datetime-local"
            value={newDue}
            onChange={e => setNewDue(e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
          />
          <textarea
            placeholder="Notes (optional)"
            value={newNotes}
            onChange={e => setNewNotes(e.target.value)}
            rows={2}
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
          />
          <button
            onClick={add}
            disabled={!newTitle.trim() || !newDue}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            Save task
          </button>
        </div>
      )}
      {loading ? (
        <p className="text-xs text-gray-400 italic">Loading…</p>
      ) : (
        <>
          {openTasks.length === 0 && completedTasks.length === 0 && (
            <p className="text-xs text-gray-400 italic">No tasks yet.</p>
          )}
          {openTasks.length > 0 && (
            <ul className="space-y-1.5">
              {openTasks.map(t => <TaskRow key={t.id} task={t} onToggle={() => toggle(t)} onDelete={() => del(t)} />)}
            </ul>
          )}
          {completedTasks.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                Completed ({completedTasks.length})
              </summary>
              <ul className="mt-2 space-y-1.5">
                {completedTasks.map(t => <TaskRow key={t.id} task={t} onToggle={() => toggle(t)} onDelete={() => del(t)} />)}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
};

const TaskRow: React.FC<{ task: Task; onToggle: () => void; onDelete: () => void }> = ({ task, onToggle, onDelete }) => {
  const done = !!task.completed_at;
  const overdue = !done && new Date(task.due_date).getTime() < Date.now();
  return (
    <li className={`flex items-center gap-2 text-xs rounded px-3 py-2 ${
      done ? 'bg-gray-50' : overdue ? 'bg-red-50' : 'bg-amber-50'
    }`}>
      <input
        type="checkbox"
        checked={done}
        onChange={onToggle}
        className="cursor-pointer"
      />
      <div className="flex-1 min-w-0">
        <div className={`font-medium ${done ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
          {task.title}
        </div>
        <div className={`text-[10px] ${overdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
          Due {formatDateTime(task.due_date)}
          {task.task_type === 'follow_up' && ' · auto from demo'}
          {overdue && ' · OVERDUE'}
        </div>
        {task.notes && <p className="text-gray-600 mt-1">{task.notes}</p>}
      </div>
      <button
        onClick={onDelete}
        className="text-gray-300 hover:text-red-600 text-sm"
        title="Delete task"
      >
        ×
      </button>
    </li>
  );
};

const CleanupParsedButton: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const run = async () => {
    if (!window.confirm(
      'Re-parse company and contact names for all existing prospects?\n\n' +
      '• Companies labelled "Co"/"Uk"/"Com" etc. will be re-derived from the email domain (so .co.uk addresses become the actual company name)\n' +
      '• Contact names that look like calendar subjects ("Alex + Joe — Demo") will be replaced with the derived name from the email local-part\n' +
      '• Manually-edited values are NOT touched\n\nProceed?'
    )) return;

    setWorking(true);
    setResult(null);
    try {
      const res = await fetch('/api/crm/prospects/cleanup-parsed', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setResult(`Scanned ${data.scanned} prospects — updated ${data.companyUpdates} companies, ${data.nameUpdates} names.`);
      onDone();
    } catch (err: any) {
      setResult(`Error: ${err.message}`);
    } finally {
      setWorking(false);
      // Clear the message after a few seconds
      setTimeout(() => setResult(null), 8000);
    }
  };

  return (
    <span className="flex items-center gap-2">
      <button
        onClick={run}
        disabled={working}
        className="text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
        title="Re-derive company + contact names from email domain. Safe — only touches old-parser artefacts."
      >
        {working ? 'Cleaning up…' : '🧹 Clean up parsed names'}
      </button>
      {result && <span className="text-[10px] text-gray-600">{result}</span>}
    </span>
  );
};

const CrmTabBtn: React.FC<{ active: boolean; onClick: () => void; label: string; hint?: string }> = ({ active, onClick, label, hint }) => (
  <button
    onClick={onClick}
    title={hint}
    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
      active
        ? 'bg-blue-600 text-white shadow-sm'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
    }`}
  >
    {label}
  </button>
);

const EraButton: React.FC<{ active: boolean; onClick: () => void; label: string }> = ({ active, onClick, label }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
      active ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
    }`}
  >
    {label}
  </button>
);

const Field: React.FC<{ label: string; value: string; onChange: (v: string) => void; type?: string }> = ({ label, value, onChange, type = 'text' }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
    />
  </div>
);

// ============================================
// Stages settings modal
// ============================================

const StagesModal: React.FC<{
  stages: Stage[];
  onClose: () => void;
  onChanged: () => void;
}> = ({ stages, onClose, onChanged }) => {
  const [working, setWorking] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newTone, setNewTone] = useState('gray');

  const patchStage = async (id: number, body: any) => {
    setWorking(true);
    try {
      const res = await fetch(`/api/crm/stages/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const e = await res.json();
        throw new Error(e.error || 'Update failed');
      }
      onChanged();
    } catch (err: any) {
      window.alert(err.message);
    } finally {
      setWorking(false);
    }
  };

  const deleteStage = async (id: number, label: string) => {
    if (!window.confirm(`Delete the "${label}" column? Any prospects in it will move to the first remaining stage.`)) return;
    setWorking(true);
    try {
      const res = await fetch(`/api/crm/stages/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) {
        const e = await res.json();
        throw new Error(e.error || 'Delete failed');
      }
      onChanged();
    } catch (err: any) {
      window.alert(err.message);
    } finally {
      setWorking(false);
    }
  };

  const addStage = async () => {
    if (!newLabel.trim()) return;
    setWorking(true);
    try {
      const res = await fetch('/api/crm/stages', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ label: newLabel.trim(), tone: newTone })
      });
      if (!res.ok) {
        const e = await res.json();
        throw new Error(e.error || 'Create failed');
      }
      setNewLabel('');
      setNewTone('gray');
      onChanged();
    } catch (err: any) {
      window.alert(err.message);
    } finally {
      setWorking(false);
    }
  };

  const move = async (idx: number, direction: -1 | 1) => {
    const target = stages[idx + direction];
    const current = stages[idx];
    if (!target || !current) return;
    // Swap display_orders
    setWorking(true);
    try {
      await fetch('/api/crm/stages/reorder', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          order: [
            { id: current.id, displayOrder: target.display_order },
            { id: target.id,  displayOrder: current.display_order }
          ]
        })
      });
      onChanged();
    } catch (err: any) {
      window.alert(err.message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Manage pipeline stages</h2>
            <p className="text-xs text-gray-500 mt-0.5">Shape the kanban to match your actual sales process.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
        </div>

        <div className="px-6 py-4 space-y-3">
          {stages.map((stage, idx) => (
            <div key={stage.id} className={`border-2 rounded-lg p-3 ${TONE_CLASSES[stage.tone] || TONE_CLASSES.gray}`}>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={stage.label}
                  onChange={e => patchStage(stage.id, { label: e.target.value })}
                  disabled={working}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-medium bg-white/70"
                />
                <select
                  value={stage.tone}
                  onChange={e => patchStage(stage.id, { tone: e.target.value })}
                  disabled={working}
                  className="px-2 py-1 border border-gray-300 rounded text-xs bg-white/70"
                >
                  {TONE_NAMES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button onClick={() => move(idx, -1)} disabled={working || idx === 0}
                  className="px-2 py-1 text-gray-600 hover:text-gray-900 disabled:opacity-30">↑</button>
                <button onClick={() => move(idx,  1)} disabled={working || idx === stages.length - 1}
                  className="px-2 py-1 text-gray-600 hover:text-gray-900 disabled:opacity-30">↓</button>
                <button
                  onClick={() => deleteStage(stage.id, stage.label)}
                  disabled={working}
                  className="px-2 py-1 text-red-600 hover:text-red-800 text-sm"
                  title="Delete stage"
                >
                  🗑
                </button>
              </div>
              <input
                type="text"
                value={stage.description || ''}
                onChange={e => patchStage(stage.id, { description: e.target.value })}
                disabled={working}
                placeholder="One-line description (optional)"
                className="w-full mt-2 px-2 py-1 border border-gray-200 rounded text-xs bg-white/70"
              />
              <div className="flex items-center gap-4 mt-2 text-xs">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={stage.is_won}
                    onChange={e => patchStage(stage.id, { isWon: e.target.checked })}
                    disabled={working}
                  />
                  <span className={stage.is_won ? 'font-semibold text-green-700' : 'text-gray-600'}>
                    Won (auto-stamps converted_at)
                  </span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={stage.is_lost}
                    onChange={e => patchStage(stage.id, { isLost: e.target.checked })}
                    disabled={working}
                  />
                  <span className={stage.is_lost ? 'font-semibold text-gray-700' : 'text-gray-600'}>
                    Lost (auto-stamps lost_at)
                  </span>
                </label>
              </div>
            </div>
          ))}

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-700 mb-2">+ Add a new stage</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                placeholder="Stage name (e.g. Follow-up, Proposal Sent)"
                className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
              <select
                value={newTone}
                onChange={e => setNewTone(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded text-xs"
              >
                {TONE_NAMES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <button
                onClick={addStage}
                disabled={working || !newLabel.trim()}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-600">
          Changes save instantly. Deleting a stage moves its prospects to the first remaining column.
        </div>
      </div>
    </div>
  );
};

export default CRM;
